// Sign-up as its own page (/signup). Step one asks who the workspace is for, recruitment agency or in-house
// team; step two is the form. /signup/agency or /signup/inhouse preselects the first step (the landing pages
// link to theirs) and ?ref=<code> (a referral link) is checked and shown on the form, as the old modal did.
import { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { MARKETING } from "../theme/ThemeContext";
import { storeEdition } from "../utils/editionLink";
import { storeReferral } from "../utils/referralLink";
import AuthShell from "../components/AuthShell";
import { SignupForm, SANS } from "./LandingPage";

const C = MARKETING;

const CHOICES = [
  { id: "agency", label: "Recruitment agency", sub: "You place people at client companies." },
  { id: "inhouse", label: "In-house team", sub: "You hire for your own company." },
];

const PANEL = {
  1: { headline: "Who are you hiring for?", sub: "Free for 7 days, no card needed." },
  2: { headline: "Sign up", sub: "Add your details and we'll email you a confirmation link to finish." },
};

const Heading = styled.h1`
  font-family: ${SANS};
  font-weight: 600;
  font-size: 24px;
  line-height: 1.1;
  letter-spacing: -0.025em;
  color: ${C.deep};
  margin: 0;
  text-align: center;
  text-wrap: balance;
`;

const Lead = styled.p`
  margin: 10px 0 26px;
  text-align: center;
  font-size: 15.5px;
  line-height: 1.55;
  color: ${C.body};
  text-wrap: pretty;
`;

// Two plain rows split by a hairline; the card around them is the only box.
const Choices = styled.div`
  display: grid;
  margin: -8px 0 0;
`;

const Choice = styled.button`
  appearance: none;
  text-align: left;
  display: grid;
  gap: 3px;
  padding: 20px 12px;
  margin: 0 -12px;
  width: calc(100% + 24px);
  border: 0;
  border-radius: 12px;
  background: none;
  font-family: ${SANS};
  cursor: pointer;
  transition: background 0.14s ease;
  & + & {
    position: relative;
    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 12px;
      right: 12px;
      height: 1px;
      background: ${C.line};
    }
  }
  &:hover,
  &.on {
    background: ${C.wash};
    &::before {
      opacity: 0;
    }
  }
  &:focus-visible {
    outline: 2px solid ${C.blue};
    outline-offset: -2px;
  }
  .label {
    font-size: 16.5px;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: ${C.deep};
  }
  .sub {
    font-size: 13.5px;
    color: ${C.muted};
  }
`;

// Above the form: the choice just made, and a way back to change it.
const Chosen = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0 0 22px;
  padding: 10px 14px;
  border-radius: 12px;
  background: ${C.wash};
  border: 1px solid ${C.line};
  font-size: 13.5px;
  color: ${C.body};
  b {
    color: ${C.deep};
    font-weight: 600;
  }
  button {
    border: 0;
    background: none;
    font: inherit;
    color: ${C.blue};
    cursor: pointer;
    padding: 0;
    min-height: 0;
  }
`;

const Switch = styled.p`
  margin: 18px 0 -8px;
  text-align: center;
  font-size: 13.5px;
  line-height: 1.55;
  color: ${C.muted};
  button {
    border: 0;
    background: none;
    font: inherit;
    color: ${C.blue};
    cursor: pointer;
    padding: 0;
    min-height: 0;
  }
`;

function readQuery(key) {
  try {
    return (new URLSearchParams(window.location.search).get(key) || "").trim();
  } catch {
    return "";
  }
}

export default function SignupPage() {
  const navigate = useNavigate();
  // Nothing is chosen until the person clicks a row, whichever path brought them here.
  const [edition, setEdition] = useState(null);
  // The log-in page sends an address it didn't recognise here, already typed.
  const [initialEmail] = useState(() => readQuery("email").toLowerCase());
  // Step one shows every time; a landing page's edition only preselects its row.
  const [step, setStep] = useState(1);
  // A referral link's code, checked and remembered so a Google/Microsoft sign-up carries it too.
  const [referral, setReferral] = useState(null);
  useEffect(() => {
    const code = readQuery("ref");
    if (!code) return undefined;
    let alive = true;
    fetch(`/api/auth/referral?code=${encodeURIComponent(code)}`, { credentials: "include" })
      .then(async (res) => {
        const body = await res.json().catch(() => ({}));
        if (!alive || !res.ok) return;
        const ref = { code: body.code, firstName: body.firstName || "", credits: body.credits || 0 };
        setReferral(ref);
        storeReferral({ code: ref.code, via: "link" });
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const choose = (id) => {
    setEdition(id);
    storeEdition(id);
    try {
      window.history.replaceState(null, "", `/signup/${id}${window.location.search}`);
    } catch {}
    setStep(2);
  };
  const chosen = CHOICES.find((c) => c.id === edition) || CHOICES[0];

  return (
    <AuthShell
      headline={PANEL[step].headline}
      sub={PANEL[step].sub}
      homeHref={edition === "inhouse" ? "/in-house-recruiters" : "/"}
      wide={step === 2}
    >

      {step === 1 ? (
        <Choices>
          {CHOICES.map((c) => (
            <Choice key={c.id} type="button" className={edition === c.id ? "on" : ""} onClick={() => choose(c.id)} aria-pressed={edition === c.id}>
              <span className="label">{c.label}</span>
              <span className="sub">{c.sub}</span>
            </Choice>
          ))}
        </Choices>
      ) : (
        <>
          <Chosen>
            <div>
              Signing up as <b>{chosen.label}</b>
            </div>
            <button type="button" onClick={() => setStep(1)}>
              Change
            </button>
          </Chosen>
          {!referral?.code && (
            <>
              <Heading>Your details</Heading>
              <Lead>Your own workspace to find and reach candidates.</Lead>
            </>
          )}
          <SignupForm key={edition} edition={edition} referral={referral} showHead={false} initialEmail={initialEmail} />
        </>
      )}
      {/* The form's own footer carries the log-in link on step two. */}
      {step === 1 && (
        <Switch>
          Already have an account?{" "}
          <button type="button" onClick={() => navigate("/login")}>
            Log in
          </button>
        </Switch>
      )}
    </AuthShell>
  );
}
